/** @jsx jsx */
import { jsx } from '@emotion/core';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { MDXProvider } from '@mdx-js/react';
import CodeBlock from '@atlaskit/code/block';
import SectionLink from '../components/section-link';
import { ThemedImgMdx } from '../../../components/themed-img';

const ContentRenderer = ({ id, body }) => {
  const components = {
    h2: (props) => <SectionLink level={2} {...props} />,
    h3: (props) => <SectionLink level={3} {...props} />,
    img: (props) => (
      <img css={{ width: '100%' }} alt={props.alt || ''} {...props} />
    ),
    ThemedImgMdx: (props) => {
      // Sets a default width of 100%
      return <ThemedImgMdx css={{ width: '100%' }} {...props} />;
    },
    code: ({ children, className, metastring }) => {
      /**
       * Sets the 'language' prop.
       * This is extracted from 'className' which is prefixed with 'language-'.
       *
       * @example
       *  ```css
       * .css-works-now {
       * padding: 20;
       * }
       * ```
       * @default 'tsx'
       */
      const language =
        className !== undefined ? className.replace('language-', '') : 'tsx';

      /**
       * Sets the 'showLineNumbers' prop.
       * This is extracted from 'metastring'.
       *
       * @example
       * ```text showLineNumbers=true
       * Line 1
       * Line 2
       * Line 3
       * ```
       * @default false
       */
      const showLineNumbers = metastring
        ? metastring.includes('showLineNumbers=true')
        : false;

      /**
       * Sets the 'highlight' prop.
       * This is extracted from 'metastring'.
       *
       * @example - Highlights line 1, 3 to 5
       * ```text highlight=1,3-5
       * 1 Yes
       * 2 No
       * 3 Yes
       * 4 Yes
       * 5 Yes
       * ```
       */
      const highlightMatches = metastring?.includes('highlight')
        ? metastring.match(/highlight=(\S*)/)
        : false;
      const highlight = highlightMatches?.[1];

      // Remove additional line break before closing back ticks
      const text =
        children !== undefined ? children.replace(/\n$/g, '') : undefined;

      return (
        <CodeBlock
          text={text}
          language={language}
          showLineNumbers={showLineNumbers}
          highlight={highlight}
        ></CodeBlock>
      );
    },
  };

  return (
    <MDXProvider components={components}>
      <MDXRenderer key={id}>{body}</MDXRenderer>
    </MDXProvider>
  );
};

export default ContentRenderer;
